import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header, Footer } from './Layout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, ArrowLeft, ChevronRight, Quote, Award } from 'lucide-react';
import { publicApi } from '../utils/api';
import { NewsWidget } from './NewsWidget';
import { SmartPrice, injectUsdIntoHtml } from './SmartPrice';
import { useFxRate } from '../context/FxRateContext';
import { ImageWithFallback } from './ui/image-with-fallback';
import { useAuth } from '../context/AuthContext';
import { SectionReveal, Stars } from './landing/motion';

export function CMSPublicPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const { user } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await publicApi.get(`/api/cms/pages/${slug}`);
      setPage(data);
    } catch (err) {
      if (err.response?.status === 404) setError('Page not found');
      else setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const visibleSections = useMemo(() => {
    if (!page?.section_data) return [];
    return page.section_data.filter(s => s.is_visible !== false);
  }, [page]);

  // Update document title / meta for SEO
  useEffect(() => {
    if (!page) return;
    const t = page.meta_title || `${page.title} — AGA`;
    document.title = t;
    if (page.meta_description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute('name', 'description');
        document.head.appendChild(m);
      }
      m.setAttribute('content', page.meta_description);
    }
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000B18]">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !page) {
    if (typeof document !== 'undefined') {
      document.title = '404 — Page Not Found';
    }
    return (
      <div className="min-h-screen bg-[#000B18]" data-testid="cms-404">
        <Header />
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
          <p className="font-outfit text-6xl sm:text-7xl font-bold text-[#00E5FF] mb-2 tracking-tight">404</p>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white mb-3">Page Not Found</h1>
          <p className="text-[#A0B4CC] mb-6 max-w-md">
            {error || `The page "/${slug}" doesn't exist or isn't published yet. Try going back to the homepage.`}
          </p>
          <Link to="/">
            <Button className="btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000B18] relative overflow-hidden">
      {/* Ambient decorations — soft aurora + drifting particles for the whole
          public site. Purely decorative; kept behind everything with z-0. */}
      {slug === 'home' && (
        <>
          <div className="aga-aurora fixed" style={{ opacity: 0.35, filter: 'blur(120px)' }} aria-hidden="true"><span /></div>
          <Stars count={14} className="fixed inset-0" />
        </>
      )}
      <Header />

      <main className="pt-20 pb-12 relative z-[2]">
        {/* Breadcrumb (hidden on home — redundant) */}
        {slug !== 'home' && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-[#5C7599]">
              <Link to="/" className="hover:text-[#00E5FF] transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white truncate">{page.title}</span>
            </div>
          </div>
        )}

        {slug === 'insurance' && user && user.role === 'member' && !user.is_paid_member && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
            <div className="rounded-xl border border-[#FFD740]/30 bg-[#FFD740]/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="insurance-free-banner">
              <p className="text-sm text-[#E6EDF5] flex-1"><b className="text-[#FFD740]">Available to Pro/Elite members only.</b> Upgrade your membership to get insurance coverage.</p>
              <Link to="/my-plan" className="rounded-full bg-[#FFD740] text-[#000B18] font-semibold px-4 py-2 text-sm hover:bg-[#FFE57F] transition-colors text-center" data-testid="insurance-upgrade-btn">Upgrade now</Link>
            </div>
          </div>
        )}

        {/* Page title (only show if no hero block is the first visible section AND not home) */}
        {visibleSections[0]?.section_type !== 'hero' && slug !== 'home' && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 sm:mb-8">
            <h1 className="font-outfit text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-3 sm:mb-4 break-words">{page.title}</h1>
            {page.description && (
              <p className="text-[#A0B4CC] text-base sm:text-lg">{page.description}</p>
            )}
          </div>
        )}

        {/* Blocks */}
        {visibleSections.length > 0 ? (
          <div className="space-y-6 sm:space-y-8" data-testid="cms-page-blocks">
            {visibleSections.map((section, i) => (
              <SectionReveal
                key={section.id || `${section.section_type}-${i}`}
                delay={Math.min(i % 3, 3)}
              >
                <CMSSection section={section} />
              </SectionReveal>
            ))}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-[#041A3B] border-white/10">
              <CardContent className="p-8">
                <p className="text-[#A0B4CC]">
                  {page.meta_description || 'Content coming soon…'}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Global Business & Economy News — shown on home page only */}
        {slug === 'home' && (
          <>
            {/* Editorial trust marquee — brand-adjacent ticker for legitimacy */}
            <SectionReveal>
              <div className="relative mt-10 py-5 overflow-hidden border-y border-[#00E5FF]/20 bg-gradient-to-r from-[#00E5FF]/5 via-[#FFB800]/5 to-[#00E5FF]/5">
                <div className="marquee whitespace-nowrap font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase">
                  {[
                    'Bank-Grade Identity Verification',
                    'PayMongo Secured Payments',
                    'Real-Time Referral Ledger',
                    'Merchant Partner Marketplace',
                    'Transparent Participation Value',
                    'Members Across The Philippines',
                  ].concat([
                    'Bank-Grade Identity Verification',
                    'PayMongo Secured Payments',
                    'Real-Time Referral Ledger',
                    'Merchant Partner Marketplace',
                    'Transparent Participation Value',
                    'Members Across The Philippines',
                  ]).map((t, i) => (
                    <span key={`trust-${i}`} className="inline-flex items-center gap-3">
                      <span className="text-[#FFB800]">◈</span>
                      <span className="marquee-item font-semibold">
                        {t}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </SectionReveal>
            <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 mt-4">
              <div className="max-w-5xl mx-auto">
                <SectionReveal>
                  <NewsWidget maxItems={6} />
                </SectionReveal>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

/* ---------------- Block renderer ---------------- */
const MAX_WIDTH_MAP = {
  narrow: 'max-w-3xl',
  default: 'max-w-5xl',
  wide: 'max-w-7xl',
  full: 'max-w-none',
};

const PADDING_Y_MAP = {
  none: 'py-0',
  small: 'py-3 sm:py-4',
  default: 'py-6 sm:py-8',
  large: 'py-8 sm:py-12 lg:py-16',
  xlarge: 'py-12 sm:py-20 lg:py-24',
};

/* ---------------- Block wrapper (extracted for stability) ---------------- */
function BlockWrapper({ section, wrapperBgStyle, overlayStyle, bgImageUrl, padyClass, roundedClass, isFull, maxWidthClass, alignClass, forceDark, children }) {
  // Flag this section as having a visually-dark surface (background image or dark bg color).
  // The light-theme stylesheet uses this attribute to know when to KEEP white text
  // (over dark imagery) vs flip to dark text (over light/transparent backgrounds).
  // `forceDark` lets specific block types (e.g. cta) declare their intrinsic
  // dark-surface aesthetic so the light theme leaves their inner text alone.
  const isDarkSurface = forceDark || !!bgImageUrl || isDarkColor(section.bg_color);
  return (
    <section
      className={`relative ${padyClass} ${roundedClass} overflow-hidden`}
      style={wrapperBgStyle}
      data-block-type={section.section_type}
      data-block-bg={isDarkSurface ? 'dark' : 'light'}
    >
      {bgImageUrl && <div className="absolute inset-0" style={overlayStyle} />}
      <div className={`relative z-10 ${isFull ? 'px-4 sm:px-6 lg:px-8' : `mx-auto ${maxWidthClass} px-4 sm:px-6 lg:px-8`} ${alignClass}`}>
        {children}
      </div>
    </section>
  );
}

/** Returns true when a hex/rgb colour string would render as visually dark. */
function isDarkColor(c) {
  if (!c) return false;
  const m = /^#?([0-9a-f]{6})$/i.exec(String(c).trim());
  if (m) {
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
    // Relative luminance (approx.)
    return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
  }
  // Treat any rgba(low,low,low,...) or named "black" / "navy" / etc. as dark
  return /rgba?\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)/i.test(c) ? false : false;
}

function CMSSection({ section }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const { phpUsdRate } = useFxRate();
  const align = section.text_align || 'left';
  const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  const justifyClass = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';
  const titleStyle = section.title_color ? { color: section.title_color } : {};
  const bodyStyle = section.text_color ? { color: section.text_color } : {};

  const bgImageUrl = section.background_image
    ? (section.background_image.startsWith('/api') ? backendUrl + section.background_image : section.background_image)
    : null;

  const wrapperBgStyle = {};
  if (section.bg_color) wrapperBgStyle.backgroundColor = section.bg_color;
  if (bgImageUrl) {
    wrapperBgStyle.backgroundImage = `url(${bgImageUrl})`;
    wrapperBgStyle.backgroundSize = section.background_size || 'cover';
    wrapperBgStyle.backgroundPosition = section.background_position || 'center center';
    wrapperBgStyle.backgroundAttachment = section.background_attachment || 'scroll';
    wrapperBgStyle.backgroundRepeat = 'no-repeat';
  }

  const overlayStyle = bgImageUrl
    ? { backgroundColor: section.overlay_color || '#000B18', opacity: section.overlay_opacity ?? 0.6 }
    : {};

  const maxWidthClass = MAX_WIDTH_MAP[section.max_width || 'default'];
  const padyClass = PADDING_Y_MAP[section.padding_y || 'default'];
  const roundedClass = section.rounded !== false ? 'rounded-2xl' : '';

  const isFull = section.max_width === 'full';

  /** Up-to-two CTA buttons */
  const ctas = (() => {
    if (!section.cta_label && !section.cta_secondary_label) return null;
    return (
      <div className={`mt-6 flex flex-wrap gap-3 ${justifyClass}`} data-testid="cms-section-ctas">
        {section.cta_label && section.cta_url && (
          <CTALink to={section.cta_url}>
            <Button className="btn-primary btn-sheen" data-testid="cms-cta-primary">
              {section.cta_label} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CTALink>
        )}
        {section.cta_secondary_label && section.cta_secondary_url && (
          <CTALink to={section.cta_secondary_url}>
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="cms-cta-secondary">
              {section.cta_secondary_label}
            </Button>
          </CTALink>
        )}
      </div>
    );
  })();

  const wrap = (children, opts = {}) => (
    <BlockWrapper
      section={section}
      wrapperBgStyle={wrapperBgStyle}
      overlayStyle={overlayStyle}
      bgImageUrl={bgImageUrl}
      padyClass={padyClass}
      roundedClass={roundedClass}
      isFull={isFull}
      maxWidthClass={maxWidthClass}
      alignClass={alignClass}
      forceDark={opts.forceDark}
    >
      {children}
    </BlockWrapper>
  );

  /* ---------- Block-type rendering ---------- */
  switch (section.section_type) {
    case 'hero':
      return (
        wrap(<>
          {section.subtitle && (
            <p className="eyebrow mb-3 sm:mb-4">{section.subtitle}</p>
          )}
          {section.title && (
            <h1 className="font-outfit text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-3 sm:mb-4 break-words leading-[1.05] tracking-tight" style={titleStyle}>
              {section.title}
            </h1>
          )}
          {section.content && (
            <div className="text-[#A0B4CC] text-base sm:text-lg max-w-3xl mx-auto prose prose-sm sm:prose-base prose-invert break-words leading-relaxed" style={bodyStyle}
              dangerouslySetInnerHTML={{ __html: injectUsdIntoHtml(section.content, phpUsdRate) }} />
          )}
          {ctas}
        </>)
      );

    case 'content':
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-3 sm:mb-4 break-words" style={titleStyle}>
              {section.title}
            </h2>
          )}
          {section.subtitle && (
            <p className="text-[#00E5FF] text-sm sm:text-base mb-3 sm:mb-4">{section.subtitle}</p>
          )}
          {section.content && (
            <div
              className="prose prose-sm sm:prose-base prose-invert max-w-none text-[#A0B4CC] leading-relaxed break-words"
              style={bodyStyle}
              dangerouslySetInnerHTML={{ __html: injectUsdIntoHtml(section.content, phpUsdRate) }}
            />
          )}
          {ctas}
        </>)
      );

    case 'html':
      return (
        wrap(<>
          <div
            className="cms-html-block"
            dangerouslySetInnerHTML={{ __html: injectUsdIntoHtml(section.html || '', phpUsdRate) }}
          />
        </>)
      );

    case 'image': {
      const src = section.image_url
        ? (section.image_url.startsWith('/api') ? backendUrl + section.image_url : section.image_url)
        : '';
      const img = src && (
        <img
          src={src}
          alt={section.image_caption || section.title || 'Image'}
          className="w-full h-auto rounded-xl"
        />
      );
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-2xl font-semibold text-white mb-4" style={titleStyle}>{section.title}</h2>
          )}
          <div className={`${alignClass}`}>
            {section.image_link
              ? <a href={section.image_link} target={section.image_link.startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer">{img}</a>
              : img}
            {section.image_caption && (
              <p className="text-[#5C7599] text-sm mt-2 italic" style={bodyStyle}>{section.image_caption}</p>
            )}
          </div>
        </>)
      );
    }

    case 'video':
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-2xl font-semibold text-white mb-4" style={titleStyle}>{section.title}</h2>
          )}
          <VideoEmbed url={section.video_url} />
        </>)
      );

    case 'gallery':
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-2xl font-semibold text-white mb-6" style={titleStyle}>{section.title}</h2>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {(section.items || []).map((item, i) => {
              const src = item.url || item.image || item;
              const resolved = typeof src === 'string' && src.startsWith('/api') ? backendUrl + src : src;
              return (
                <div key={`${item.url || i}`} className="aspect-square rounded-lg overflow-hidden bg-[#041A3B]">
                  <ImageWithFallback
                    src={resolved}
                    alt={item.title || `Gallery ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              );
            })}
          </div>
        </>)
      );

    case 'features':
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-2 sm:mb-3 break-words leading-[1.1] tracking-tight" style={titleStyle}>{section.title}</h2>
          )}
          {section.subtitle && (
            <p className="text-[#A0B4CC] text-sm sm:text-base mb-8 sm:mb-10 max-w-2xl" style={bodyStyle}>{section.subtitle}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 text-left">
            {(section.items || []).map((item, i) => (
              <Card key={`feat-${item.title || i}`} className="tilt-card card-glow-border group relative overflow-hidden rounded-2xl bg-[#041A3B]/60 backdrop-blur border-white/[0.08] hover:border-white/20 transition-all duration-500">
                <div className="absolute left-0 top-0 h-full w-[3px] bg-[#00E5FF]/50 group-hover:w-[5px] group-hover:bg-[#00E5FF] transition-all duration-500" />
                <CardContent className="p-6 sm:p-7">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-[#5C7599] uppercase block mb-3">
                    {String(i + 1).padStart(2, '0')} · Feature
                  </span>
                  <h4 className="font-outfit font-semibold text-lg text-white mb-2 break-words leading-tight">{item.title || item}</h4>
                  {item.description && (
                    <div className="text-[#A0B4CC] text-sm prose prose-sm prose-invert max-w-none break-words leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: injectUsdIntoHtml(item.description, phpUsdRate) }} />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {ctas}
        </>)
      );

    case 'columns': {
      const cols = section.columns || [];
      const count = section.columns_count || cols.length || 2;
      const gridClass = count === 4 ? 'sm:grid-cols-2 md:grid-cols-4'
        : count === 3 ? 'sm:grid-cols-2 md:grid-cols-3'
        : 'sm:grid-cols-2';
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-2 sm:mb-3 break-words" style={titleStyle}>{section.title}</h2>
          )}
          {section.subtitle && (
            <p className="text-[#A0B4CC] text-sm sm:text-base mb-6 sm:mb-8" style={bodyStyle}>{section.subtitle}</p>
          )}
          <div className={`grid grid-cols-1 ${gridClass} gap-4 sm:gap-6 text-left`}>
            {cols.map((c, i) => {
              const colImg = c.image
                ? (c.image.startsWith('/api') ? backendUrl + c.image : c.image)
                : null;
              return (
                <Card key={`col-${i}`} className="bg-[#041A3B] border-white/10 overflow-hidden">
                  {colImg && (
                    <ImageWithFallback
                      src={colImg}
                      alt={c.title || `col-${i}`}
                      className="w-full h-32 sm:h-40 object-cover"
                      fallbackClassName="w-full h-32 sm:h-40"
                    />
                  )}
                  <CardContent className="p-4 sm:p-5">
                    {c.title && <h4 className="text-white font-medium mb-2 break-words">{c.title}</h4>}
                    {c.content && (
                      <div className="text-[#A0B4CC] text-sm prose prose-sm prose-invert max-w-none break-words"
                        dangerouslySetInnerHTML={{ __html: injectUsdIntoHtml(c.content, phpUsdRate) }} />
                    )}
                    {c.cta_label && c.cta_url && (
                      <div className="mt-3 sm:mt-4">
                        <CTALink to={c.cta_url}>
                          <Button size="sm" className="btn-secondary">
                            {c.cta_label} <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </CTALink>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {ctas}
        </>)
      );
    }

    case 'cta':
      return (
        wrap(<>
          <div
            className="keep-dark relative overflow-hidden rounded-[32px] border border-white/10 aga-grain p-6 sm:p-12 md:p-16"
            style={{
              background: 'radial-gradient(120% 100% at 0% 0%, rgba(0,229,255,0.18), transparent 55%), radial-gradient(120% 100% at 100% 100%, rgba(255,184,0,0.18), transparent 55%), linear-gradient(180deg, #051f45 0%, #000B18 100%)',
            }}
          >
            {/* animated gradient border ring */}
            <div className="absolute inset-0 rounded-[32px] pointer-events-none" aria-hidden="true"
              style={{
                background: 'linear-gradient(135deg, rgba(0,229,255,0.4), rgba(255,184,0,0.3), rgba(0,122,255,0.3))',
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                padding: '1px',
              }}
            />
            {/* corner sparkles — hidden on mobile to avoid title overlap */}
            <div className="hidden sm:flex absolute top-6 right-6 items-center gap-2 z-[3]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800] animate-pulse" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFB800]">Limited Offer</span>
            </div>
            <div className="relative z-[2]">
              {section.title && (
                <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-4 break-words leading-[1.02] tracking-tight" style={titleStyle}>
                  {section.title}
                </h2>
              )}
              {section.subtitle && (
                <p className="text-[#A0B4CC] text-base sm:text-lg mb-4 max-w-2xl leading-relaxed" style={bodyStyle}>
                  <SmartPrice text={section.subtitle} usdClassName="text-[#7DD3FC] font-medium" />
                </p>
              )}
              {section.content && (
                <div className="prose prose-sm sm:prose-base prose-invert max-w-2xl text-[#A0B4CC] break-words leading-relaxed" style={bodyStyle}
                  dangerouslySetInnerHTML={{ __html: injectUsdIntoHtml(section.content, phpUsdRate) }} />
              )}
              {ctas}
            </div>
          </div>
        </>, { forceDark: true })
      );

    case 'faq':
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-4 sm:mb-6 break-words" style={titleStyle}>{section.title}</h2>
          )}
          <div className="space-y-2 sm:space-y-3">
            {(section.items || []).map((item, i) => (
              <details key={`faq-${i}`} className="bg-[#041A3B] border border-white/10 rounded-lg group">
                <summary className="cursor-pointer p-3 sm:p-4 text-white text-sm sm:text-base font-medium flex items-center justify-between gap-3">
                  <span className="break-words flex-1">{item.question || item.title || `Question ${i + 1}`}</span>
                  <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div
                  className="px-3 sm:px-4 pb-3 sm:pb-4 text-[#A0B4CC] text-sm break-words [&_a]:text-[#00E5FF] [&_a]:underline [&_b]:text-white"
                  dangerouslySetInnerHTML={{ __html: injectUsdIntoHtml(item.answer || item.description || '', phpUsdRate) }}
                />
              </details>
            ))}
          </div>
        </>)
      );

    case 'testimonials':
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-6 sm:mb-8 break-words" style={titleStyle}>{section.title}</h2>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {(section.items || []).map((item, i) => (
              <Card key={`tm-${i}`} className="bg-[#041A3B] border-white/10">
                <CardContent className="p-5 sm:p-6">
                  <Quote className="w-5 h-5 sm:w-6 sm:h-6 text-[#00E5FF] mb-3" />
                  <p className="text-[#A0B4CC] text-sm sm:text-base italic mb-4 leading-relaxed break-words">&ldquo;{item.quote}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    {item.avatar && (
                      <ImageWithFallback
                        src={item.avatar}
                        alt={item.name}
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover"
                        fallback={
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#000B18] border border-white/10 flex items-center justify-center text-[#00E5FF] text-sm font-semibold">
                            {(item.name || '?').charAt(0).toUpperCase()}
                          </div>
                        }
                      />
                    )}
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{item.name}</p>
                      {item.role && <p className="text-[#5C7599] text-xs truncate">{item.role}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>)
      );

    case 'pricing':
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-6 sm:mb-8 break-words" style={titleStyle}>{section.title}</h2>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {(section.items || []).map((item, i) => (
              <Card key={`pr-${i}`} className="bg-[#041A3B] border-white/10">
                <CardContent className="p-5 sm:p-6 text-center">
                  <h3 className="font-outfit text-lg sm:text-xl text-white mb-2 break-words">{item.title}</h3>
                  <p className="text-2xl sm:text-3xl font-bold text-[#00E5FF] mb-3">
                    <SmartPrice text={item.price} usdClassName="text-sm text-[#A0B4CC] font-medium" />
                  </p>
                  <p className="text-[#A0B4CC] text-sm break-words">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>)
      );

    case 'plans_grid':
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-2 sm:mb-4 break-words" style={titleStyle}>{section.title}</h2>
          )}
          {section.subtitle && (
            <p className="text-[#A0B4CC] text-sm sm:text-base text-center mb-6">{section.subtitle}</p>
          )}
          <div id="plans">
            <PlansGridBlock />
          </div>
        </>)
      );

    case 'divider':
      return (
        wrap(<>
          <hr className="border-white/15" />
        </>)
      );

    case 'spacer':
      return <div style={{ height: section.padding_y === 'xlarge' ? '6rem' : section.padding_y === 'large' ? '4rem' : section.padding_y === 'small' ? '1rem' : '2rem' }} />;

    default:
      return (
        wrap(<>
          {section.title && (
            <h2 className="font-outfit text-2xl font-semibold text-white mb-4" style={titleStyle}>{section.title}</h2>
          )}
          {section.content && (
            <div className="prose prose-invert max-w-none text-[#A0B4CC]" style={bodyStyle}
              dangerouslySetInnerHTML={{ __html: injectUsdIntoHtml(section.content, phpUsdRate) }} />
          )}
          {ctas}
        </>)
      );
  }
}

/* Helper: internal link uses <Link>, external/anchor uses <a> */
function CTALink({ to, children }) {
  if (!to) return children;
  const isExternal = /^https?:\/\//i.test(to);
  if (isExternal) return <a href={to} target="_blank" rel="noopener noreferrer">{children}</a>;
  return <Link to={to}>{children}</Link>;
}

/* Helper: video embed (YouTube/Vimeo/raw mp4) */
function VideoEmbed({ url }) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (ytMatch) {
    return (
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        <iframe
          className="absolute inset-0 w-full h-full rounded-xl"
          src={`https://www.youtube.com/embed/${ytMatch[1]}`}
          title="YouTube video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (vimeoMatch) {
    return (
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        <iframe
          className="absolute inset-0 w-full h-full rounded-xl"
          src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
          title="Vimeo video"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <video controls className="w-full rounded-xl">
      <source src={url} />
      Your browser does not support the video tag.
    </video>
  );
}

/**
 * Renders live subscription plan cards inside any CMS-driven page.
 * Used for the editable Pricing page so admins can rearrange surrounding copy
 * (hero / FAQ / disclaimer) while the plan cards stay 100% data-driven.
 */
function PlansGridBlock() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get('/api/subscriptions/plans');
        // Only display active plans, sorted by admin-defined order then price.
        const active = (data.plans || [])
          .filter(p => p.is_active !== false)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.price || 0) - (b.price || 0));
        setPlans(active);
      } catch (e) { /* keep empty */ }
      setLoading(false);
    })();
  }, []);

  // Convert duration_days → human label ("per year", "per 6 years", "per 30 days").
  // Tolerates ±5 days of slippage (leap years, half-years) when matching whole years.
  const cycleLabel = (days) => {
    if (!days || days <= 0) return '';
    if (days >= 360) {
      const years = days / 365;
      const rounded = Math.round(years);
      if (Math.abs(years - rounded) * 365 <= 5) {
        return rounded === 1 ? 'per year' : `per ${rounded} years`;
      }
      return `per ${years.toFixed(1)} years`;
    }
    if (days % 30 === 0) {
      const months = days / 30;
      return months === 1 ? 'per month' : `per ${months} months`;
    }
    return `per ${days} days`;
  };

  const onSubscribe = async (planId) => {
    const token = localStorage.getItem('aga_token');
    if (!token) {
      window.location.href = '/login';
      return;
    }
    setSubscribing(planId);
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/checkout`, {
        method: 'POST', headers, body: JSON.stringify({ plan_id: planId }),
      });
      const data = await r.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { /* swallow; toast handled elsewhere */ }
    setSubscribing(null);
  };

  if (loading) {
    return (
      <div className="py-12 text-center" data-testid="plans-grid-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF] mx-auto" />
      </div>
    );
  }
  if (!plans.length) {
    return (
      <p className="text-[#5C7599] text-center py-8 text-sm" data-testid="plans-grid-empty">
        No plans available right now. Please check back soon.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="plans-grid">
      {plans.map((p) => {
        const isPopular = !!(p.is_popular || p.is_featured);
        return (
          <Card key={p.id} className={`bg-[#041A3B] border ${isPopular ? 'border-[#00E5FF]/60 shadow-[0_0_28px_rgba(0,229,255,0.18)]' : 'border-white/10'}`} data-testid={`plan-card-${p.id}`}>
            <CardContent className="p-6 sm:p-7">
              {isPopular && (
                <span className="inline-block text-[10px] uppercase tracking-widest font-bold text-[#000B18] bg-[#00E5FF] px-2 py-0.5 rounded mb-3" data-testid={`plan-popular-${p.id}`}>Most Popular</span>
              )}
              <h3 className="font-outfit text-xl sm:text-2xl text-white mb-1 break-words">{p.name}</h3>
              {p.description && (
                <p className="text-[#A0B4CC] text-sm mb-4" data-testid={`plan-description-${p.id}`}>{p.description}</p>
              )}
              <p className="text-3xl sm:text-4xl font-bold text-[#00E5FF] mb-1">
                <SmartPrice text={`₱${(p.price || 0).toLocaleString()}`} usdClassName="text-sm text-[#A0B4CC] font-medium ml-2" />
              </p>
              <p className="text-[#5C7599] text-xs mb-5" data-testid={`plan-cycle-${p.id}`}>{cycleLabel(p.duration_days) || 'one-time'}</p>
              {p.cert_cap_usd > 0 && (
                <div
                  className="mb-5 flex items-center gap-2 px-3 py-2 rounded-md bg-gradient-to-r from-[#FFEA00]/10 to-[#FFEA00]/5 border border-[#FFEA00]/30"
                  data-testid={`plan-cert-cap-${p.id}`}
                >
                  <Award className="w-4 h-4 text-[#FFEA00] flex-shrink-0" />
                  <div className="text-xs leading-tight">
                    <span className="text-[#FFEA00] font-semibold">
                      Earn up to ${Number(p.cert_cap_usd).toLocaleString()} stock certificate
                    </span>
                    <span className="text-[#A0B4CC] block sm:inline">
                      {' '}· {p.cert_chunks} × ${p.cert_chunk_usd} chunks
                    </span>
                  </div>
                </div>
              )}
              {(p.features || []).length > 0 && (
                <ul className="space-y-2 mb-6">
                  {(p.features || []).map((f, i) => (
                    <li key={`${p.id}-feat-${i}`} className="text-[#A0B4CC] text-sm flex items-start gap-2">
                      <span className="text-[#00E676] mt-0.5 flex-shrink-0">✓</span>
                      <span className="break-words">{f}</span>
                    </li>
                  ))}
                </ul>
              )}
              <Button
                onClick={() => onSubscribe(p.id)}
                disabled={subscribing === p.id}
                className={`w-full ${isPopular ? 'bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90' : 'bg-[#041A3B] text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/10'}`}
                data-testid={`subscribe-${p.id}-btn`}
              >
                {subscribing === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : `Choose ${p.name}`}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default CMSPublicPage;
